import { useState, useEffect } from 'react';
import { Moon, RefreshCw, Loader as Loader2, ChevronDown } from 'lucide-react';
import { getUserPreferences, upsertUserPreferences } from '@/lib/api';

const SYNC_OPTIONS = [
  { value: 0, label: 'Real-time sync' },
  { value: 5, label: 'Every 5 min' },
  { value: 15, label: 'Every 15 min' },
  { value: 30, label: 'Every 30 min' },
  { value: 60, label: 'Every 60 min' },
];

export default function PreferencesSection() {
  const [darkMode, setDarkMode] = useState(false);
  const [syncInterval, setSyncInterval] = useState(30);
  const [loaded, setLoaded] = useState(false);
  const [savingDark, setSavingDark] = useState(false);
  const [savingSync, setSavingSync] = useState(false);

  useEffect(() => {
    getUserPreferences().then((prefs) => {
      if (prefs) {
        setDarkMode(prefs.dark_mode);
        setSyncInterval(prefs.auto_sync_interval);
      }
      setLoaded(true);
    });
  }, []);

  async function toggleDarkMode() {
    const next = !darkMode;
    setDarkMode(next);
    setSavingDark(true);
    try {
      await upsertUserPreferences({ dark_mode: next });
      if (next) {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
    } catch {
      setDarkMode(!next);
    } finally {
      setSavingDark(false);
    }
  }

  async function handleSyncChange(value: number) {
    setSyncInterval(value);
    setSavingSync(true);
    try {
      await upsertUserPreferences({ auto_sync_interval: value });
    } catch {
      setSyncInterval(syncInterval);
    } finally {
      setSavingSync(false);
    }
  }

  if (!loaded) {
    return (
      <div className="flex items-center gap-3 p-4">
        <Loader2 size={16} className="animate-spin text-white" />
        <span className="text-sm text-white">Loading preferences...</span>
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-[11px] font-semibold text-white uppercase tracking-wider mb-2.5 px-1">Preferences</h2>
      <div className="glass-panel rounded-2xl p-2">
        <div className="flex items-center gap-3 py-2.5 px-3">
          <div className="w-9 h-9 rounded-xl bg-teal-500/10 flex items-center justify-center flex-shrink-0">
            <Moon size={15} className="text-teal-400" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-white">Dark Mode</p>
            <p className="text-[11px] text-white">Toggle dark appearance</p>
          </div>
          <button
            onClick={toggleDarkMode}
            disabled={savingDark}
            className={`relative w-11 h-6 rounded-full transition-colors duration-200 flex-shrink-0 ${darkMode ? 'bg-teal-500' : 'bg-slate-300'}`}
          >
            <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow-sm transition-transform duration-200 flex items-center justify-center ${darkMode ? 'translate-x-5' : 'translate-x-0'}`}>
              {savingDark && <Loader2 size={10} className="animate-spin text-white" />}
            </div>
          </button>
        </div>

        <div className="h-px bg-white/10 mx-3 my-1" />

        <div className="flex items-center gap-3 py-2.5 px-3">
          <div className="w-9 h-9 rounded-xl bg-teal-500/10 flex items-center justify-center flex-shrink-0">
            <RefreshCw size={15} className="text-teal-400" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-white">Auto-Sync Stripe</p>
            <p className="text-[11px] text-white">
              {syncInterval === 0 ? 'Updates instantly via webhook' : 'Sync frequency for transactions'}
            </p>
          </div>
          <div className="relative flex-shrink-0">
            <select
              value={syncInterval}
              onChange={(e) => handleSyncChange(Number(e.target.value))}
              disabled={savingSync}
              className="appearance-none glass-inner border border-white/10 rounded-xl px-3 py-1.5 pr-7 text-xs font-medium text-white focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 outline-none transition-all duration-200"
            >
              {SYNC_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            <ChevronDown size={12} className="absolute right-2 top-1/2 -translate-y-1/2 text-white pointer-events-none" />
          </div>
        </div>
      </div>
    </div>
  );
}
